<script lang="ts" setup>
import { defineAsyncComponent } from 'vue'
import { useSwiper } from '~/composables/common/use-swiper'
import { useSports } from '~/composables/sports/use-sports'
import { LOBBY_GAME_URLS } from '~/configs/page-url'
import { formatTimeUtc, StringUtils } from '~/utils'
import { DATE_TIME_FORMAT } from '~/constants'
import { SLUG_ALL_SPORTS, SPORTS_LIMIT } from '~composables/sports/interface'

const WrapperSchedulesSports = defineAsyncComponent(async () => await import('./wrapper.vue'))
const BaseImage = defineAsyncComponent(async () => await import('~/components/common/base-image.vue'))
const LoadingSpin = defineAsyncComponent(async () => await import('~common/loading-spin.vue'))

const { modules } = useSwiper()
const {
  setScheduleMatches,
  categorySchedules,
  scheduleSports,
  playASportGame,
  openKSportGame,
  leagueName,
  changeLeagueName,
  onSwiperLeagues,
  isLoadingSchedule,
  totalSchedule,
  loadMoreSchedules,
  checkTimeUpcoming
} = useSports()

useAsyncData('match-schedule', async () => {
  await setScheduleMatches()
  return { loaded: true }
})
</script>
<template>
  <WrapperSchedulesSports>
    <div class="content-schedules table-schedules">
      <div class="schedules">
        <LoadingSpin v-if="isLoadingSchedule" />
        <div v-if="!isLoadingSchedule" class="base-title flex-box gap-8 mb-4">
          <NuxtLink :to="LOBBY_GAME_URLS.SPORTS" class="icon-back">
            <span class="chevron-left" />
          </NuxtLink>
          <h2 class="base-title__text fw-700">LỊCH THI ĐẤU</h2>
        </div>
        <div v-if="categorySchedules?.length" class="schedules__category flex-box">
          <div class="table-schedules__prev schedules-swiper-button">
            <img src="/assets/images/icons/icon-arrow-right.svg" alt="icon" />
          </div>
          <Swiper
            :modules="modules"
            @swiper="onSwiperLeagues"
            :slidesPerView="'auto'"
            :spaceBetween="10"
            :navigation="{
              nextEl: '.table-schedules__next',
              prevEl: '.table-schedules__prev'
            }"
            class="nav-category"
          >
            <SwiperSlide v-for="(item, index) in categorySchedules" :key="item.slug">
              <div
                class="nav-category__link cursor-pointer"
                :class="{ active: leagueName === item.slug || (!leagueName && index === 0) }"
                @click="changeLeagueName(item.slug, index)"
              >
                <BaseImage
                  :src="item.thumb"
                  class="nav-category__logo"
                  :class="item.slug"
                  imageDefault="/assets/images/common/no-item.png"
                  :alt="item.name"
                />
                <span class="nav-category__name line-limit-1" :title="StringUtils.capitalizeWords(item.name)">{{
                  item.name
                }}</span>
              </div>
            </SwiperSlide>
          </Swiper>
          <div class="table-schedules__next schedules-swiper-button">
            <img src="/assets/images/icons/icon-arrow-right.svg" alt="icon" />
          </div>
        </div>
        <div class="schedules__content">
          <template v-if="scheduleSports.length">
            <div v-for="(schedule, index) in scheduleSports" :key="schedule.leagueName" class="collapse-item">
              <div
                class="header-collapse"
                data-bs-toggle="collapse"
                :data-bs-target="`#schedulesCollapseOpen-${index}`"
                aria-expanded="true"
                :aria-controls="`schedulesCollapseOpen-${index}`"
                role="button"
                data-toggle="collapse"
                :href="`#schedulesCollapseOpen-${index}`"
              >
                <div class="header-collapse__league">
                  <div class="header-collapse__icon-arrow">
                    <BaseImage
                      :src="schedule.thumb"
                      imageDefault="/assets/images/common/no-item.png"
                      :alt="schedule.leagueName"
                    />
                  </div>
                  <div class="header-collapse__league-name line-limit-1">{{ schedule.leagueName?.toLowerCase() }}</div>
                  <i class="icon-chev-down" />
                </div>
                <div class="header-collapse__table flex-box gap-20">
                  <div class="flex-box gap-16 header-collapse__table-header">
                    <div class="header-collapse__table-item">Chấp H1</div>
                    <div class="header-collapse__table-item">Tài xỉu H1</div>
                    <div class="header-collapse__table-item">1x2 H1</div>
                  </div>
                  <div class="flex-box gap-16 header-collapse__table-header">
                    <div class="header-collapse__table-item">Chấp TT</div>
                    <div class="header-collapse__table-item">Tài xỉu TT</div>
                    <div class="header-collapse__table-item">1X2 TT</div>
                  </div>
                </div>
              </div>
              <div :id="`schedulesCollapseOpen-${index}`" :class="['accordion-collapse collapse', 'show']">
                <div class="schedules__wrapper-item">
                  <div v-for="match in schedule.matches" :key="match.matchId" class="match-item">
                    <div class="match-item__table-team">
                      <div class="match-item__date" :class="match.live && 'ctn-live'">
                        <div v-if="checkTimeUpcoming(match.text_time)" class="match-item__upcoming" />
                        <div class="flex-box column-gap-2" v-if="match.live">
                          <div class="match-item__live">
                            <img src="/assets/images/sports/icon-live.svg" alt="Live" />
                          </div>
                          <div class="match-item__date-time">{{ match.timePlaying }}"</div>
                        </div>
                        <div v-else class="flex-box">
                          <div class="match-item__date-time">
                            {{
                              formatTimeUtc(
                                match.text_time,
                                DATE_TIME_FORMAT.FULL_DATE_FORMAT_SCHEDULE,
                                DATE_TIME_FORMAT.TIME_DATE_FORMAT_SCHEDULE
                              )
                            }}
                          </div>
                        </div>
                        <div class="match-item__full-date">
                          {{
                            match.text_time
                              ? formatTimeUtc(
                                  match.text_time,
                                  DATE_TIME_FORMAT.FULL_DATE_FORMAT_SCHEDULE,
                                  DATE_TIME_FORMAT.DATE_FORMAT
                                )
                              : ''
                          }}
                        </div>
                      </div>
                      <div class="match-item__wrapper-team">
                        <div class="match-item__team-logo">
                          <div class="match-item__team--logo">
                            <BaseImage
                              :src="match.teams[0]?.flag_thumbnail"
                              imageDefault="/assets/images/common/no-item.png"
                              :alt="match.teams[0]?.name"
                            />
                          </div>
                          <div class="match-item__team--logo">
                            <BaseImage
                              :src="match.teams[1]?.flag_thumbnail"
                              imageDefault="/assets/images/common/no-item.png"
                              :alt="match.teams[1]?.name"
                            />
                          </div>
                        </div>
                        <div class="match-item__team">
                          <div class="match-item__team--name line-limit-1">
                            {{ match.teams[0]?.name }}
                          </div>
                          <div class="match-item__team--name line-limit-1">
                            {{ match.teams[1]?.name }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="match-item__wrapper-left">
                      <div class="match-item__group-number">
                        <div class="match-item__group-rate">
                          <div class="flex-box wrapper-box">
                            <div
                              class="button"
                              :class="
                                Number(match?.ahHT?.hTeam?.rate)
                                  ? Number(match?.ahHT?.hTeam?.rate) > 0
                                    ? 'win'
                                    : 'lose'
                                  : 'disable-rate'
                              "
                            >
                              {{ match?.ahHT?.hTeam?.rate ?? '--' }}
                            </div>
                            <div class="button" :class="!match?.overHT?.hTeam?.rate && 'disable-rate'">
                              {{ match?.overHT?.hTeam?.rate ?? '--' }}
                            </div>
                            <div
                              class="button"
                              :class="
                                Number(match?.winHT?.hTeam?.rate)
                                  ? Number(match?.winHT?.hTeam?.rate) > 0
                                    ? 'win'
                                    : 'lose'
                                  : 'disable-rate'
                              "
                            >
                              {{ match?.winHT?.hTeam?.rate ?? '--' }}
                            </div>
                          </div>
                          <div class="flex-box wrapper-box">
                            <div
                              class="button"
                              :class="
                                Number(match?.ahFT?.hTeam?.rate)
                                  ? Number(match?.ahFT?.hTeam?.rate) > 0
                                    ? 'win'
                                    : 'lose'
                                  : 'disable-rate'
                              "
                            >
                              {{ match?.ahFT?.hTeam?.rate ?? '--' }}
                            </div>
                            <div class="button" :class="!match?.overFT?.hTeam?.rate && 'disable-rate'">
                              {{ match?.overFT?.hTeam?.rate ?? '--' }}
                            </div>
                            <div
                              class="button"
                              :class="
                                Number(match?.winFT?.hTeam?.rate)
                                  ? Number(match?.winFT?.hTeam?.rate) > 0
                                    ? 'win'
                                    : 'lose'
                                  : 'disable-rate'
                              "
                            >
                              {{ match?.winFT?.hTeam?.rate ?? '--' }}
                            </div>
                          </div>
                        </div>
                        <div class="match-item__group-rate">
                          <div class="flex-box wrapper-box">
                            <div
                              class="button"
                              :class="
                                Number(match?.athena_ahHT?.hTeam?.rate)
                                  ? Number(match?.athena_ahHT?.hTeam?.rate) > 0
                                    ? 'win'
                                    : 'lose'
                                  : 'disable-rate'
                              "
                            >
                              {{ match?.athena_ahHT?.hTeam?.rate ?? '--' }}
                            </div>
                            <div class="button" :class="!match?.athena_overHT?.hTeam?.rate && 'disable-rate'">
                              {{ match?.athena_overHT?.hTeam?.rate ?? '--' }}
                            </div>
                            <div
                              class="button"
                              :class="
                                Number(match?.athena_winHT?.hTeam?.rate)
                                  ? Number(match?.athena_winHT?.hTeam?.rate) > 0
                                    ? 'win'
                                    : 'lose'
                                  : 'disable-rate'
                              "
                            >
                              {{ match?.athena_winHT?.hTeam?.rate ?? '--' }}
                            </div>
                          </div>
                          <div class="flex-box wrapper-box">
                            <div
                              class="button"
                              :class="
                                Number(match?.athena_ahFT?.hTeam?.rate)
                                  ? Number(match?.athena_ahFT?.hTeam?.rate) > 0
                                    ? 'win'
                                    : 'lose'
                                  : 'disable-rate'
                              "
                            >
                              {{ match?.athena_ahFT?.hTeam?.rate ?? '--' }}
                            </div>
                            <div class="button" :class="!match?.athena_overFT?.hTeam?.rate && 'disable-rate'">
                              {{ match?.athena_overFT?.hTeam?.rate ?? '--' }}
                            </div>
                            <div
                              class="button"
                              :class="
                                Number(match?.athena_winFT?.hTeam?.rate)
                                  ? Number(match?.athena_winFT?.hTeam?.rate) > 0
                                    ? 'win'
                                    : 'lose'
                                  : 'disable-rate'
                              "
                            >
                              {{ match?.athena_winFT?.hTeam?.rate ?? '--' }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="match-item__play-game flex-box gap-8">
                        <div class="match-item__ctn-button-sports">
                          <button
                            class="button main main--primary"
                            :disabled="
                              !match?.ahHT?.hTeam?.rate &&
                              !match?.overHT?.hTeam?.rate &&
                              !match?.overFT?.hTeam?.rate &&
                              !match?.ahFT?.hTeam?.rate &&
                              !match?.winFT?.hTeam?.rate &&
                              !match?.winHT?.hTeam?.rate
                            "
                            :class="{
                              disabled:
                                !match?.ahHT?.hTeam?.rate &&
                                !match?.overHT?.hTeam?.rate &&
                                !match?.overFT?.hTeam?.rate &&
                                !match?.ahFT?.hTeam?.rate &&
                                !match?.winFT?.hTeam?.rate &&
                                !match?.winHT?.hTeam?.rate
                            }"
                            @click="openKSportGame(match)"
                          >
                            K-SPORTS
                          </button>
                          <button
                            class="button btn-sport"
                            :disabled="
                              !match?.athena_ahFT?.hTeam?.rate &&
                              !match?.athena_ahHT?.hTeam?.rate &&
                              !match?.athena_overFT?.hTeam?.rate &&
                              !match?.athena_overHT?.hTeam?.rate &&
                              !match?.athena_winFT?.hTeam?.rate &&
                              !match?.athena_winHT?.hTeam?.rate
                            "
                            :class="{
                              disabled:
                                !match?.athena_ahFT?.hTeam?.rate &&
                                !match?.athena_ahHT?.hTeam?.rate &&
                                !match?.athena_overFT?.hTeam?.rate &&
                                !match?.athena_overHT?.hTeam?.rate &&
                                !match?.athena_winFT?.hTeam?.rate &&
                                !match?.athena_winHT?.hTeam?.rate
                            }"
                            @click="playASportGame(match)"
                          >
                            A-SPORTS
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <div class="no-data" v-if="!scheduleSports.length && !isLoadingSchedule">
            <div class="no-data__image">
              <img src="/assets/images/sports/nodata.png" alt="Icon News" />
            </div>
            <div class="no-data__title">Lịch thi đấu đang được cập nhật</div>
          </div>
          <div
            v-if="
              totalSchedule > SPORTS_LIMIT &&
              totalSchedule > scheduleSports?.length &&
              (leagueName === SLUG_ALL_SPORTS || !leagueName)
            "
            class="text-center mt-3"
          >
            <button class="button load-more-schedule m-auto" @click="loadMoreSchedules">Xem thêm</button>
          </div>
        </div>
      </div>
    </div>
  </WrapperSchedulesSports>
</template>
<style scoped lang="scss">
@import '~scssAsset/desktop/pages/sports/schedules.scss';
</style>
